import React, { useMemo, useRef, useState } from 'react';
import { Button, Modal, message } from 'antd';
import type { TCredentialingOverview } from 'types/credentialing';
import SignatureModal from 'components/ProviderSignature/SignatureModal';
import { bindCredentialingSignature } from 'api/credentialing';
import useCurrentUser from 'hooks/useCurrentUser';
import { isGroupAdmin } from 'utils/common';
import SignatureBlock, {
    SELF_CONSENT,
    bindLatestSignature,
    loadProviderSignature,
    type TSignatureRecord,
    type TSignee,
} from 'components/CredentialingModal/components/AgreementStep/SignatureBlock';
import { ArrowSm, WarnIcon } from '../SetupReminderBanner';
import s from '../SetupReminderBanner/s.module.less';
import c from './s.module.less';

type Props = {
    overview: TCredentialingOverview;
    currentProviderId?: number;
    isGroup: boolean;
    // 签名有变化后静默重拉 overview，横幅据此收起
    onChanged: () => void;
};

const nameOf = (p: { firstName?: string; lastName?: string; email?: string }) =>
    [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || '';

// 原型 signatureBackfillBannerHTML（10/26）：签名上线前就完成 onboarding 的 provider 没有签名，
// Credentialing 页顶部黄色横幅提醒。solo 直接打开 Add signature 弹窗；group 打开一个 Signatures 弹窗，
// 每个 provider 一个 chip（没签的红点），签完自动跳到下一个没签的人。
// 后端 #20：overview 的 hasSignature 指 credentialing 有没有绑定签名，不是 EHR 里有没有——EHR 有签名
// 但没绑定也要提示；COMPLETED 且 hasSignature=false 才算缺。确认时调 /onboarding/signature 绑定，
// 只有 practice owner / admin 能调，所以 group 里其他成员不显示。外层 Cancel 只是关闭（10/27 定）
export default function SignatureBackfillBanner({ overview, currentProviderId, isGroup, onChanged }: Props) {
    const [soloOpen, setSoloOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState(false);
    const [soloInitial, setSoloInitial] = useState<TSignatureRecord | null>(null);
    const [sigs, setSigs] = useState<Record<number, TSignatureRecord | null>>({});
    const [binding, setBinding] = useState(false);
    const [currentUser] = useCurrentUser();
    const canBind = !(currentUser?.groupPractice && !isGroupAdmin(currentUser));
    // 弹窗打开那一刻没签的人：Confirm 至少补了其中一个才可点
    const missingAtOpen = useRef<number[]>([]);

    const providers = useMemo(() => {
        const seen = new Set<number>();
        return (overview.providers || []).filter((p) => {
            if (seen.has(p.providerId)) return false;
            seen.add(p.providerId);
            return true;
        });
    }, [overview.providers]);

    const completed = providers.filter((p) => p.onboardingStatus === 'COMPLETED');
    const group = isGroup && completed.length > 1;
    const signees: TSignee[] = completed.map((p) => ({
        providerId: p.providerId,
        name: nameOf(p),
        self: p.providerId === currentProviderId,
    }));
    let missing: number[] = [];
    if (group) missing = completed.filter((p) => p.hasSignature === false).map((p) => p.providerId);
    else if (overview.hasSignature === false && completed.length > 0 && currentProviderId != null)
        missing = [currentProviderId];
    if (!canBind || (!missing.length && !soloOpen && !groupOpen)) return null;

    // 打开时缺绑定、现在有签名（新签的，或 EHR 里本来就有）的人，Confirm 一次提交
    const toBind = missingAtOpen.current
        .filter((id) => sigs[id]?.id)
        .map((id) => ({ providerId: id, signatureId: sigs[id]!.id as number }));
    const closeGroup = () => {
        setGroupOpen(false);
        onChanged();
    };
    const confirmGroup = async () => {
        setBinding(true);
        try {
            const res: any = await bindCredentialingSignature(toBind);
            if (res?.error) {
                message.error(res.error);
                return;
            }
            closeGroup();
            message.success('Signatures added');
        } finally {
            setBinding(false);
        }
    };

    return (
        <>
            {missing.length > 0 && (
                <div className={`${s.banner} ${s.warning}`}>
                    <WarnIcon />
                    <span className={s.text}>
                        {group
                            ? 'Add your providers’ signatures. Payers need them on the enrollment forms we file.'
                            : 'Add your signature. Payers need it on the enrollment forms we file for you.'}
                    </span>
                    <span className={s.actions}>
                        <button
                            type="button"
                            className={s.action}
                            onClick={() => {
                                if (group) {
                                    missingAtOpen.current = missing;
                                    setSigs({});
                                    setGroupOpen(true);
                                } else {
                                    // EHR 里已有签名就带进弹窗，确认后照样绑定
                                    loadProviderSignature().then((r) => {
                                        setSoloInitial(r);
                                        setSoloOpen(true);
                                    });
                                }
                            }}
                        >
                            {group ? 'Add signatures' : 'Add signature'}
                            <ArrowSm />
                        </button>
                    </span>
                </div>
            )}

            {soloOpen && (
                <SignatureModal
                    open
                    onCancel={() => setSoloOpen(false)}
                    onConfirm={async () => {
                        setSoloOpen(false);
                        if (currentProviderId != null && (await bindLatestSignature(currentProviderId))) onChanged();
                    }}
                    initialData={soloInitial || undefined}
                    consentText={SELF_CONSENT}
                />
            )}

            <Modal
                open={groupOpen}
                title="Signatures"
                width={640}
                destroyOnClose
                onCancel={closeGroup}
                footer={
                    <>
                        <Button onClick={closeGroup}>Cancel</Button>
                        <Button type="primary" disabled={!toBind.length} loading={binding} onClick={confirmGroup}>
                            Confirm
                        </Button>
                    </>
                }
            >
                <p className={c.desc}>
                    Payers need each provider’s own signature on the enrollment forms we file for them.
                </p>
                <SignatureBlock signees={signees} group bare advanceOnSign onChange={setSigs} />
            </Modal>
        </>
    );
}
